/**
 * Vertragsbaukasten, Schritt 3 (Editor „Vorlage bauen“): reine Logik ohne Oberfläche.
 * Bauplan: docs/vertragsbaukasten/bericht-vertraege.md, Teil 3.3 und 3.4; Ansicht docs/vertragsbaukasten/ansicht/.
 *
 * Der Editor arbeitet nur mit Bausteinen (packages/domain contract-blocks.ts). Beim Speichern übersetzt compileBlocks sie in
 * genau die Vorlagenfelder, die heute geprüft, versendet, unterschrieben und gehasht werden (body_html, annex_html,
 * annex_hidden_from, variables, required_signers). Kein Code an der Oberfläche: Felder sind Chips mit Klartext, Bedingungen
 * sind Sätze, Sichtbarkeit ist ausgeschrieben.
 */
import {
  CONTRACT_ROLES, SYSTEM_VARIABLES, compileBlocks, checkTemplateBlocks, convertTemplate, fieldPath, isSystemVariablePath, systemVariable,
  templateActivationBlocked, validateBlocks, walkBlocks, childLists,
  type Block, type BlockType, type ContractRole, type CustomVariableDef, type FieldRef, type Finding, type Inline, type InlineMark, type Language,
  type ListItem, type VariableType,
} from "@talentkontor/domain";
import { t, nameIn, istCreatorKonto } from "../../../../lib/sprache";
import { money } from "../../../../lib/format";
import { ROLE_LABEL } from "../modell";

// ---------------------------------------------------------------------------
// Kennungen
// ---------------------------------------------------------------------------

let zaehler = 0;
/** Neue Kennung eines Bausteins (Schema: [A-Za-z0-9_-]{1,40}), eindeutig gegenüber `vorhanden`. */
export function neueId(vorhanden: ReadonlySet<string> = new Set(), prefix = "b"): string {
  for (;;) {
    zaehler += 1;
    const id = `${prefix}${Date.now().toString(36).slice(-5)}${zaehler.toString(36)}`;
    if (!vorhanden.has(id)) return id;
  }
}

/**
 * Alle Kennungen im Dokument (Bausteine und Listenpunkte) und die vergebenen Schlüssel als `key:<schlüssel>` (Wahlklauseln,
 * dazu die eigenen Angaben aus `defs`): neue Wahlklauseln bekommen so nie einen schon vergebenen Schlüssel.
 */
export function alleIds(blocks: readonly Block[], defs: readonly CustomVariableDef[] = []): Set<string> {
  const ids = new Set<string>();
  for (const { block } of walkBlocks(blocks)) {
    ids.add(block.id);
    if (block.type === "list") for (const it of block.items) ids.add(it.id);
    if (block.type === "choice") ids.add(`key:${block.key}`);
  }
  for (const d of defs) ids.add(`key:${d.key}`);
  return ids;
}

// ---------------------------------------------------------------------------
// Bausteine anlegen („/“-Menü)
// ---------------------------------------------------------------------------

/** Bausteine, die das „/“-Menü anbietet (Freitext aus alter Vorlage entsteht nur beim Umwandeln). */
export type NeuerTyp = Exclude<BlockType, "legacy_html" | "clause">;

export interface MenuePunkt {
  typ: NeuerTyp;
  label: string;
  hilfe: string;
  /** Suchwörter (beide Sprachen) */
  woerter: string;
  zeichen: string;
}

export function bausteinMenue(): MenuePunkt[] {
  return [
    { typ: "heading", zeichen: "H", label: t("Überschrift", "Heading"), hilfe: t("Titel, Paragraph oder Zwischenüberschrift", "Title, section or subheading"), woerter: "ueberschrift überschrift titel paragraph heading title section" },
    { typ: "paragraph", zeichen: "¶", label: t("Absatz", "Paragraph"), hilfe: t("Fließtext mit Feldern", "Running text with fields"), woerter: "absatz text klausel paragraph clause" },
    { typ: "list", zeichen: "1.", label: t("Aufzählung", "List"), hilfe: t("nummerierte Punkte oder Spiegelstriche", "numbered points or bullets"), woerter: "aufzaehlung aufzählung liste punkte list bullets numbered" },
    { typ: "table", zeichen: "▦", label: t("Tabelle", "Table"), hilfe: t("Leistungen, Vergütung, Nutzungsrechte", "Services, fees, usage rights"), woerter: "tabelle table leistungen verguetung vergütung" },
    { typ: "field", zeichen: "{ }", label: t("Feld", "Field"), hilfe: t("eine Zeile „Bezeichnung: Wert“", "one line “Label: value”"), woerter: "feld angabe wert field value" },
    { typ: "choice", zeichen: "☑", label: t("Wählbare Klausel", "Optional clause"), hilfe: t("beim Erstellen an- oder abwählbar", "can be switched on or off when creating"), woerter: "waehlbare wählbare klausel option choice optional clause" },
    { typ: "condition", zeichen: "⑂", label: t("Nur wenn …", "Only if …"), hilfe: t("erscheint nur, wenn die Bedingung zutrifft", "appears only if the condition applies"), woerter: "nur wenn bedingung sonst condition only if else" },
    { typ: "private", zeichen: "◐", label: t("Vertraulicher Teil", "Confidential part"), hilfe: t("sehen nur die gewählten Parteien", "only the chosen parties see it"), woerter: "vertraulich verborgen privat confidential private hidden" },
    { typ: "attachment", zeichen: "📎", label: t("Anlage", "Annex"), hilfe: t("Bestandteil, den alle Parteien sehen", "part that all parties see"), woerter: "anlage anhang annex attachment" },
    { typ: "signatures", zeichen: "✍", label: t("Unterschriften", "Signatures"), hilfe: t("ein Feld je Partei; wer hier steht, unterschreibt", "one field per party; whoever is listed signs"), woerter: "unterschrift unterschriften signatur signature signatures" },
    { typ: "pagebreak", zeichen: "⤓", label: t("Seitenumbruch", "Page break"), hilfe: t("neue Seite im PDF", "new page in the PDF"), woerter: "seitenumbruch seite umbruch page break" },
    { typ: "note", zeichen: "✎", label: t("Interner Hinweis", "Internal note"), hilfe: t("nur im Editor, nie im Vertrag", "only in the editor, never in the contract"), woerter: "hinweis notiz intern note internal" },
  ];
}

/** Menüpunkte zur Suche (Groß-/Kleinschreibung und Umlaute egal). */
export function filtereMenue(punkte: readonly MenuePunkt[], suche: string): MenuePunkt[] {
  const q = normal(suche);
  if (!q) return [...punkte];
  return punkte.filter((p) => normal(`${p.label} ${p.hilfe} ${p.woerter}`).includes(q));
}

function normal(s: string): string {
  return s.toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/ß/g, "ss").trim();
}

const txt = (v: string): Inline[] => (v ? [{ t: "text", v }] : []);

/** Neuer Baustein mit sinnvollem Anfang; Kennungen eindeutig gegenüber dem Dokument. */
export function neuerBaustein(typ: NeuerTyp, ids: Set<string>, lang: Language, vorhandeneRollen: readonly ContractRole[] = CONTRACT_ROLES): Block {
  const id = neueId(ids);
  ids.add(id);
  const kind = (): Block => {
    const p = neueId(ids);
    ids.add(p);
    return { id: p, type: "paragraph", text: [] };
  };
  switch (typ) {
    case "heading": return { id, type: "heading", level: 2, text: [] };
    case "paragraph": return { id, type: "paragraph", text: [] };
    case "note": return { id, type: "note", text: [] };
    case "list": {
      const a = neueId(ids); ids.add(a);
      return { id, type: "list", ordered: true, items: [{ id: a, text: [] }] };
    }
    case "table": return {
      id, type: "table",
      header: [txt(lang === "en" ? "Service" : "Leistung"), txt(lang === "en" ? "Fee" : "Vergütung")],
      rows: [[[], []]],
    };
    case "field": return { id, type: "field", label: lang === "en" ? "Label" : "Bezeichnung", field: { src: "system", path: "deal.title" } };
    case "choice": {
      const key = choiceKey(ids);
      return { id, type: "choice", key, question: { de: "Diese Klausel aufnehmen?", en: "Include this clause?" }, default: true, children: [kind()] };
    }
    case "condition": return { id, type: "condition", when: { src: "system", path: "talent.is_minor" }, children: [kind()] };
    case "private": return { id, type: "private", visibleTo: ["agency", "brand"], children: [kind()] };
    case "attachment": return { id, type: "attachment", title: lang === "en" ? "Service description" : "Leistungsbeschreibung", children: [kind()] };
    case "signatures": return { id, type: "signatures", parties: [...vorhandeneRollen] };
    case "pagebreak": return { id, type: "pagebreak" };
  }
}

/** Schlüssel einer neuen wählbaren Klausel (eigene Ja/Nein-Angabe), nie sichtbar. */
function choiceKey(ids: Set<string>): string {
  for (let i = 1; ; i++) {
    const k = `wahl_${i}`;
    if (!ids.has(`key:${k}`)) { ids.add(`key:${k}`); return k; }
  }
}

/** Schlüssel aus einer Bezeichnung („Veröffentlichung bis“ → veroeffentlichung_bis), eindeutig. */
export function schluesselAus(label: string, vergeben: ReadonlySet<string>): string {
  const base = (label.toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").replace(/^[^a-z]+/, "") || "feld").slice(0, 50);
  if (!vergeben.has(base)) return base;
  for (let i = 2; ; i++) if (!vergeben.has(`${base}_${i}`)) return `${base}_${i}`;
}

// ---------------------------------------------------------------------------
// Baum-Operationen (unveränderlich)
// ---------------------------------------------------------------------------

/** Ort eines Bausteins: Liste (Kette von Eltern-Kennungen und Zweig) und Index. */
export interface Ort { eltern: string | null; zweig: 0 | 1; index: number }

export function finde(blocks: readonly Block[], id: string): Block | null {
  for (const { block } of walkBlocks(blocks)) if (block.id === id) return block;
  return null;
}

export function ortVon(blocks: readonly Block[], id: string, eltern: string | null = null, zweig: 0 | 1 = 0): Ort | null {
  const i = blocks.findIndex((b) => b.id === id);
  if (i >= 0) return { eltern, zweig, index: i };
  for (const b of blocks) {
    const lists = childLists(b);
    for (let z = 0; z < lists.length; z++) {
      const r = ortVon(lists[z]!, id, b.id, z as 0 | 1);
      if (r) return r;
    }
  }
  return null;
}

function mitKindern(b: Block, zweig: 0 | 1, kinder: Block[]): Block {
  switch (b.type) {
    case "condition": return zweig === 0 ? { ...b, children: kinder } : { ...b, otherwise: kinder };
    case "choice": case "private": case "attachment": case "clause": return { ...b, children: kinder };
    default: return b;
  }
}

/** Liste an einem Ort ersetzen (eltern null = oberste Ebene). */
function ersetzeListe(blocks: readonly Block[], eltern: string | null, zweig: 0 | 1, fn: (list: Block[]) => Block[]): Block[] {
  if (eltern === null) return fn([...blocks]);
  return blocks.map((b) => {
    if (b.id === eltern) {
      const lists = childLists(b);
      return mitKindern(b, zweig, fn([...(lists[zweig] ?? [])]));
    }
    const lists = childLists(b);
    if (!lists.length) return b;
    let out = b;
    lists.forEach((l, z) => {
      const neu = ersetzeListe(l, eltern, zweig, fn);
      if (neu.some((x, k) => x !== l[k]) || neu.length !== l.length) out = mitKindern(out, z as 0 | 1, neu);
    });
    return out;
  });
}

export function aendere(blocks: readonly Block[], id: string, fn: (b: Block) => Block): Block[] {
  const o = ortVon(blocks, id);
  if (!o) return [...blocks];
  return ersetzeListe(blocks, o.eltern, o.zweig, (l) => l.map((b) => (b.id === id ? fn(b) : b)));
}

export function entferne(blocks: readonly Block[], id: string): Block[] {
  const o = ortVon(blocks, id);
  if (!o) return [...blocks];
  return ersetzeListe(blocks, o.eltern, o.zweig, (l) => l.filter((b) => b.id !== id));
}

/** Nach `nachId` einfügen (null: am Ende der obersten Ebene; vor Unterschriften, falls vorhanden). */
export function fuegeEin(blocks: readonly Block[], nachId: string | null, neu: Block): Block[] {
  if (nachId === null) {
    const sig = blocks.findIndex((b) => b.type === "signatures");
    if (sig >= 0 && neu.type !== "signatures" && neu.type !== "private") return [...blocks.slice(0, sig), neu, ...blocks.slice(sig)];
    return [...blocks, neu];
  }
  const o = ortVon(blocks, nachId);
  if (!o) return [...blocks, neu];
  // vertraulicher Teil nur auf oberster Ebene (Stufe 1)
  if (neu.type === "private" && o.eltern !== null) return fuegeEin(blocks, null, neu);
  return ersetzeListe(blocks, o.eltern, o.zweig, (l) => [...l.slice(0, o.index + 1), neu, ...l.slice(o.index + 1)]);
}

/** In einen Container (am Ende seines Zweigs) einfügen. */
export function fuegeInContainer(blocks: readonly Block[], containerId: string, zweig: 0 | 1, neu: Block): Block[] {
  if (neu.type === "private") return fuegeEin(blocks, null, neu);
  return ersetzeListe(blocks, containerId, zweig, (l) => [...l, neu]);
}

/**
 * Darf ein Baustein dieser Art an diese Stelle? Vertraulicher Teil nur einmal und nur auf oberster Ebene; Unterschriften und
 * Anlagen nie in einem Container (Unterschriften gehören ans Ende, die Nummer einer Anlage hinge sonst von einer Bedingung ab).
 * `inContainer` ist der Container (null = oberste Ebene).
 */
export function erlaubtAn(blocks: readonly Block[], typ: NeuerTyp, inContainer: string | null): boolean {
  if (typ === "private") return inContainer === null && !blocks.some((b) => b.type === "private");
  if (typ === "signatures" || typ === "attachment") return inContainer === null;
  return true;
}

/** Container, in dem ein Baustein steht (null = oberste Ebene). */
export function containerVon(blocks: readonly Block[], id: string): string | null {
  return ortVon(blocks, id)?.eltern ?? null;
}

/** Innerhalb der eigenen Liste um eins nach oben (-1) oder unten (+1). */
export function verschiebe(blocks: readonly Block[], id: string, richtung: -1 | 1): Block[] {
  const o = ortVon(blocks, id);
  if (!o) return [...blocks];
  return ersetzeListe(blocks, o.eltern, o.zweig, (l) => {
    const j = o.index + richtung;
    if (j < 0 || j >= l.length) return l;
    const next = [...l];
    [next[o.index], next[j]] = [next[j]!, next[o.index]!];
    return next;
  });
}

/** Kopie mit neuen Kennungen direkt dahinter (Strg/Cmd+D). */
export function dupliziere(blocks: readonly Block[], id: string): Block[] {
  const b = finde(blocks, id);
  if (!b || b.type === "private" || b.type === "signatures") return [...blocks];
  const ids = alleIds(blocks);
  return fuegeEin(blocks, id, neuKennzeichnen(b, ids));
}

function neuKennzeichnen(b: Block, ids: Set<string>): Block {
  const id = neueId(ids); ids.add(id);
  const kids = (l: Block[]) => l.map((x) => neuKennzeichnen(x, ids));
  switch (b.type) {
    case "list": return { ...b, id, items: b.items.map((it) => { const i = neueId(ids); ids.add(i); return { ...it, id: i }; }) };
    case "condition": return { ...b, id, children: kids(b.children), ...(b.otherwise ? { otherwise: kids(b.otherwise) } : {}) };
    case "choice": return { ...b, id, key: choiceKey(ids), children: kids(b.children) };
    case "private": case "attachment": case "clause": return { ...b, id, children: kids(b.children) };
    default: return { ...b, id };
  }
}

function keysOf(blocks: readonly Block[]): Set<string> {
  const out = new Set<string>();
  for (const { block } of walkBlocks(blocks)) if (block.type === "choice") out.add(block.key);
  return out;
}

/** Klausel aus der Bibliothek lösen: ihre Bausteine stehen danach frei im Dokument. */
export function loeseKlausel(blocks: readonly Block[], id: string): Block[] {
  const o = ortVon(blocks, id);
  const b = finde(blocks, id);
  if (!o || !b || b.type !== "clause") return [...blocks];
  return ersetzeListe(blocks, o.eltern, o.zweig, (l) => [...l.slice(0, o.index), ...b.children, ...l.slice(o.index + 1)]);
}

// ---------------------------------------------------------------------------
// Felder: Katalog, Beschriftung, Formate
// ---------------------------------------------------------------------------

export type FeldArt = "system" | "erstellen" | "unterzeichner";

export interface FeldEintrag {
  ref: FieldRef;
  label: string;
  art: FeldArt;
  gruppe: string;
  typ: VariableType;
  beispiel: string;
}

const NAMESPACE_LABEL: Record<string, { de: string; en: string }> = {
  get agency() { return istCreatorKonto() ? { de: "Dein Konto", en: "Your account" } : { de: "Agentur", en: "Agency" }; },
  talent: { de: "Creator", en: "Creator" },
  brand: { de: "Marke", en: "Brand" },
  deal: { de: "Kooperation", en: "Collaboration" },
  contract: { de: "Vertrag", en: "Contract" },
  today: { de: "Datum", en: "Date" },
};

export function namespaceLabel(ns: string): string {
  const l = NAMESPACE_LABEL[ns];
  return l ? t(l.de, l.en) : ns;
}

/** Formatierung passend zum Typ (Betrag, Datum, Prozent, mehrzeilig); der Chip wählt sie selbst. */
export function standardFilter(typ: VariableType): string[] | undefined {
  switch (typ) {
    case "money": return ["money"];
    case "date": return ["date"];
    case "percent": return ["percent"];
    case "lines": return ["lines"];
    default: return undefined;
  }
}

function beispielText(v: unknown, typ: VariableType): string {
  if (v === undefined || v === null || v === "") return "";
  if (typ === "money" && typeof v === "number") return money(v, "EUR");
  if (typ === "boolean") return v ? t("ja", "yes") : t("nein", "no");
  if (typ === "date" && typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return `${v.slice(8, 10)}.${v.slice(5, 7)}.${v.slice(0, 4)}`;
  if (typ === "percent") return `${String(v).replace(".", ",")} %`;
  return String(v).split("\n")[0]!;
}

/** Feldkatalog für die Auswahl über „{“: automatisch nach Herkunft, beim Erstellen, vom Unterzeichner. */
export function feldKatalog(defs: readonly CustomVariableDef[]): FeldEintrag[] {
  const out: FeldEintrag[] = [];
  for (const v of SYSTEM_VARIABLES) {
    const ns = v.path.split(".")[0]!;
    out.push({ ref: { src: "system", path: v.path }, label: nameIn(v.label.de, v.label.en), art: "system", gruppe: namespaceLabel(ns), typ: v.type, beispiel: beispielText(v.example, v.type) });
  }
  for (const d of defs) {
    if ((d as { ui?: string }).ui === "choice") continue;
    const art: FeldArt = d.fillable_by ? "unterzeichner" : "erstellen";
    out.push({
      ref: { src: "custom", path: d.key }, label: nameIn(d.label.de, d.label.en) || d.key, art,
      gruppe: art === "unterzeichner" ? t(`füllt ${ROLE_LABEL[d.fillable_by!]} aus`, `filled in by ${ROLE_LABEL[d.fillable_by!]}`) : t("beim Erstellen ausfüllen", "filled in when creating"),
      typ: d.type, beispiel: beispielText(d.default, d.type),
    });
  }
  return out;
}

export function filtereFelder(felder: readonly FeldEintrag[], suche: string): FeldEintrag[] {
  const q = normal(suche);
  if (!q) return [...felder];
  return felder.filter((f) => normal(`${f.label} ${f.gruppe}`).includes(q));
}

/** Klartext eines Felds (nie der Code). */
export function feldLabel(ref: FieldRef, defs: readonly CustomVariableDef[]): string {
  if (ref.src === "custom") {
    const d = defs.find((x) => x.key === ref.path);
    return d ? nameIn(d.label.de, d.label.en) || d.label.de || ref.path : t("Unbekanntes Feld", "Unknown field");
  }
  const s = systemVariable(ref.path);
  return s ? nameIn(s.label.de, s.label.en) : t("Unbekanntes Feld", "Unknown field");
}

export function feldArt(ref: FieldRef, defs: readonly CustomVariableDef[]): FeldArt {
  if (ref.src === "system") return "system";
  return defs.find((d) => d.key === ref.path)?.fillable_by ? "unterzeichner" : "erstellen";
}

/** Schon genutzte Felder (Reihenfolge des Dokuments, ohne Doppelte). */
export function genutzteFelder(blocks: readonly Block[]): FieldRef[] {
  const seen = new Map<string, FieldRef>();
  const visit = (nodes: readonly Inline[]) => {
    for (const n of nodes) {
      if (n.t === "field") seen.set(fieldPath(n.field), n.field);
      if (n.t === "if") { visit(n.then); if (n.else) visit(n.else); }
    }
  };
  for (const { block } of walkBlocks(blocks)) {
    for (const text of texteVon(block)) visit(text);
    if (block.type === "field") seen.set(fieldPath(block.field), block.field);
  }
  return [...seen.values()];
}

/** Alle Texte eines Bausteins (ohne Kinder). */
export function texteVon(b: Block): Inline[][] {
  switch (b.type) {
    case "heading": case "paragraph": case "note": return [b.text];
    case "list": return b.items.map((it) => it.text);
    case "table": return [...(b.header ?? []), ...b.rows.flat()];
    default: return [];
  }
}

export interface NeuesFeld { label: string; typ: VariableType; wer: "agency_create" | ContractRole; pflicht: boolean }

/** Neues Feld „+ Neues Feld“: Definition mit Schlüssel aus der Bezeichnung; zweite Sprache übernimmt die erste. */
export function neuesFeld(f: NeuesFeld, defs: readonly CustomVariableDef[], lang: Language): CustomVariableDef {
  const key = schluesselAus(f.label, new Set(defs.map((d) => d.key)));
  const label = f.label.trim().slice(0, 120);
  return {
    key, label: lang === "en" ? { de: label, en: label } : { de: label, en: label }, type: f.typ, required: f.pflicht,
    ...(f.wer !== "agency_create" ? { fillable_by: f.wer } : {}),
  };
}

// ---------------------------------------------------------------------------
// Bedingungen in Klartext
// ---------------------------------------------------------------------------

export interface Bedingung { ref: FieldRef; satz: string }

const BEDINGUNGEN: readonly { path: string; de: string; en: string }[] = [
  { path: "talent.is_minor", de: "der Creator minderjährig ist", en: "the creator is a minor" },
  { path: "deal.exclusive", de: "Exklusivität vereinbart ist", en: "exclusivity is agreed" },
  { path: "deal.is_barter", de: "die Vergütung in Produkten besteht", en: "the fee is paid in products" },
  { path: "deal.is_commission_only", de: "nur Provision vereinbart ist", en: "only commission is agreed" },
  { path: "deal.agency_fee", de: "eine Agenturgebühr vereinbart ist", en: "an agency fee is agreed" },
  { path: "talent.company_name", de: "der Creator über eine eigene Firma abrechnet", en: "the creator invoices through their own company" },
  { path: "contract.auto_renew", de: "sich der Vertrag automatisch verlängert", en: "the contract renews automatically" },
  { path: "contract.tail_months", de: "ein Nachlauf vereinbart ist", en: "a tail period is agreed" },
  { path: "brand.represented_by", de: "die Marke von einer Agentur vertreten wird", en: "the brand is represented by an agency" },
  { path: "agency.law_de", de: "deutsches Recht gilt", en: "German law applies" },
  { path: "agency.law_at", de: "österreichisches Recht gilt", en: "Austrian law applies" },
  { path: "agency.law_ch", de: "Schweizer Recht gilt", en: "Swiss law applies" },
  { path: "agency.seat_de", de: "die Agentur ihren Sitz in Deutschland hat", en: "the agency is based in Germany" },
];

/** Auswahl für „Nur wenn …“: feste Sätze (nur Pfade, die der Katalog kennt) und jedes eigene Feld. */
export function bedingungen(defs: readonly CustomVariableDef[]): Bedingung[] {
  const out: Bedingung[] = BEDINGUNGEN.filter((b) => isSystemVariablePath(b.path)).map((b) => ({ ref: { src: "system", path: b.path }, satz: t(b.de, b.en) }));
  for (const d of defs) {
    if ((d as { ui?: string }).ui === "choice") continue;
    const name = nameIn(d.label.de, d.label.en) || d.key;
    out.push({ ref: { src: "custom", path: d.key }, satz: d.type === "boolean" ? t(`„${name}“ Ja ist`, `“${name}” is yes`) : t(`„${name}“ ausgefüllt ist`, `“${name}” is filled in`) });
  }
  return out;
}

/** Gibt es für diese Bedingung einen festen Klartext-Satz (statt „‚Feld‘ zutrifft“)? */
export function istKlartextBedingung(ref: FieldRef): boolean {
  return ref.src === "system" && BEDINGUNGEN.some((b) => b.path === ref.path);
}

/** Satz zu einer Bedingung („Nur wenn der Creator minderjährig ist“). */
export function bedingungSatz(ref: FieldRef, defs: readonly CustomVariableDef[]): string {
  const hit = bedingungen(defs).find((b) => b.ref.src === ref.src && b.ref.path === ref.path);
  if (hit) return hit.satz;
  return t(`„${feldLabel(ref, defs)}“ zutrifft`, `“${feldLabel(ref, defs)}” applies`);
}

// ---------------------------------------------------------------------------
// Sichtbarkeit (vertraulicher Teil, Stufe 1)
// ---------------------------------------------------------------------------

/** „Nur für Marke und Agentur sichtbar. Der Creator sieht diesen Teil nicht.“ */
export function sichtbarkeitText(visibleTo: readonly ContractRole[]): string {
  const sehen = CONTRACT_ROLES.filter((r) => visibleTo.includes(r));
  const nicht = CONTRACT_ROLES.filter((r) => !visibleTo.includes(r));
  const liste = (rs: readonly ContractRole[]) => rs.map((r) => ROLE_LABEL[r]).join(t(" und ", " and "));
  if (!nicht.length) return t("Für alle Parteien sichtbar.", "Visible to all parties.");
  const nichtSatz = nicht.length === 1
    ? NICHT_SICHTBAR[nicht[0]!]()
    : t(`${liste(nicht)} sehen diesen Teil nicht.`, `${liste(nicht)} do not see this part.`);
  return t(`Nur für ${liste(sehen)} sichtbar. `, `Only visible to ${liste(sehen)}. `) + nichtSatz;
}

const NICHT_SICHTBAR: Record<ContractRole, () => string> = {
  brand: () => t("Die Marke sieht diesen Teil nicht.", "The brand does not see this part."),
  talent: () => t("Der Creator sieht diesen Teil nicht.", "The creator does not see this part."),
  agency: () => t("Die Agentur sieht diesen Teil nicht.", "The agency does not see this part."),
};

// ---------------------------------------------------------------------------
// Gliederung
// ---------------------------------------------------------------------------

export interface GliederungsPunkt { id: string; titel: string; tiefe: number; art: BlockType; merkmal: string | null }

/** Überschriften, Container, vertraulicher Teil und Unterschriften als Gliederung (links). */
export function gliederung(blocks: readonly Block[], defs: readonly CustomVariableDef[]): GliederungsPunkt[] {
  const out: GliederungsPunkt[] = [];
  const text = (nodes: readonly Inline[]) => nodes.map((n) => (n.t === "text" ? n.v : n.t === "field" ? feldLabel(n.field, defs) : n.t === "br" ? " " : "")).join("").trim();
  for (const { block, parents } of walkBlocks(blocks)) {
    const tiefe = parents.length;
    switch (block.type) {
      case "heading": out.push({ id: block.id, titel: text(block.text) || t("(Überschrift ohne Text)", "(heading without text)"), tiefe, art: "heading", merkmal: null }); break;
      case "choice": out.push({ id: block.id, titel: nameIn(block.question.de, block.question.en) || t("Wählbare Klausel", "Optional clause"), tiefe, art: "choice", merkmal: t("wählbar", "optional") }); break;
      case "condition": out.push({ id: block.id, titel: t(`Nur wenn ${bedingungSatz(block.when, defs)}`, `Only if ${bedingungSatz(block.when, defs)}`), tiefe, art: "condition", merkmal: t("wenn", "if") }); break;
      case "private": out.push({ id: block.id, titel: block.title || t("Vertraulicher Teil", "Confidential part"), tiefe, art: "private", merkmal: t("vertraulich", "confidential") }); break;
      case "attachment": out.push({ id: block.id, titel: block.title, tiefe, art: "attachment", merkmal: t("Anlage", "Annex") }); break;
      case "clause": out.push({ id: block.id, titel: t("Klausel aus der Bibliothek", "Clause from the library"), tiefe, art: "clause", merkmal: null }); break;
      case "signatures": out.push({ id: block.id, titel: t("Unterschriften", "Signatures"), tiefe, art: "signatures", merkmal: null }); break;
      case "field": out.push({ id: block.id, titel: block.label || t("Feld", "Field"), tiefe, art: "field", merkmal: t("Feld", "field") }); break;
      case "pagebreak": out.push({ id: block.id, titel: t("Seitenumbruch", "Page break"), tiefe, art: "pagebreak", merkmal: null }); break;
      case "legacy_html": out.push({ id: block.id, titel: t("Freitext aus alter Vorlage", "Free text from the old template"), tiefe, art: "legacy_html", merkmal: null }); break;
      default: break;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Übersetzen, Prüfen, Speichern
// ---------------------------------------------------------------------------

export interface VorlagenFelder {
  blocks: Block[];
  blocks_schema: 1;
  body_html: string;
  annex_html: string | null;
  annex_hidden_from: ContractRole[];
  variables: CustomVariableDef[];
  required_signers: ContractRole[];
}

/**
 * Was gespeichert wird: Bausteine und ihre Übersetzung. Eigene Angaben, die im Dokument nicht mehr vorkommen, bleiben erhalten
 * (sie stören nicht und gehen beim Löschen eines Chips nicht verloren); Wahlklauseln ohne Baustein fallen weg.
 */
export function speicherFelder(blocks: readonly Block[], defs: readonly CustomVariableDef[], lang: Language): VorlagenFelder {
  const keys = keysOf(blocks);
  // Eine Wahl-Angabe ohne Baustein „wählbare Klausel“ ist verwaist, außer der Text nutzt sie noch (etwa „Nur wenn“ im Satz aus einer
  // umgewandelten Vorlage): dann bleibt sie, sonst fehlte ihr Platzhalter beim Aktivieren (Befund 1 Schritt 8)
  const genutzt = new Set<string>();
  const roh = compileBlocks(blocks, { language: lang, variables: [...defs] });
  for (const m of `${roh.bodyHtml}\n${roh.annexHtml ?? ""}`.matchAll(/\bvar\.([A-Za-z0-9_]+)/g)) genutzt.add(m[1]!);
  const ohneVerwaisteWahl = defs.filter((d) => (d as { ui?: string }).ui !== "choice" || keys.has(d.key) || genutzt.has(d.key));
  const c = compileBlocks(blocks, { language: lang, variables: ohneVerwaisteWahl });
  return {
    blocks: [...blocks], blocks_schema: 1, body_html: c.bodyHtml, annex_html: c.annexHtml,
    annex_hidden_from: c.annexHiddenFrom, variables: c.variables, required_signers: c.requiredSigners,
  };
}

export interface PruefPunkt {
  finding: Finding;
  sperrt: boolean;
  /** Knopf zur Behebung */
  behebung: null | { art: "unterschrift"; rolle: ContractRole } | { art: "bedingt"; feld: string; baustein: string } | { art: "unterschriften-anlegen" } | { art: "zeigen"; baustein: string };
}

/** Prüfung vor dem Veröffentlichen (Fachlogik wie der Server) mit Behebungsvorschlag. */
export function pruefe(blocks: readonly Block[], defs: readonly CustomVariableDef[], lang: Language): { punkte: PruefPunkt[]; sperrt: boolean } {
  const findings = [...checkTemplateBlocks(blocks, { language: lang, variables: defs }), ...editorBefunde(blocks)];
  const hatSignatur = blocks.some((b) => b.type === "signatures");
  const punkte = findings.map((f): PruefPunkt => {
    const p = f.params ?? {};
    let behebung: PruefPunkt["behebung"] = null;
    if (f.code === "MISSING_SIGNATURE_BLOCK") behebung = hatSignatur ? { art: "unterschrift", rolle: p["role"] as ContractRole } : { art: "unterschriften-anlegen" };
    if (f.code === "NO_SIGNERS") behebung = { art: "unterschriften-anlegen" };
    if (f.code === "INVALID_BLOCKS") {
      const id = /@([A-Za-z0-9_-]+)$/.exec(String(p["problem"] ?? ""))?.[1];
      if (id && finde(blocks, id)) behebung = { art: "zeigen", baustein: id };
    }
    if ((f.code === "ATTACHMENT_IN_CONTAINER" || f.code === "PRIVATE_PART_EMPTY") && finde(blocks, String(p["block"]))) behebung = { art: "zeigen", baustein: String(p["block"]) };
    if (f.code === "OPTIONAL_FIELD_IN_SENTENCE") behebung = { art: "bedingt", feld: String(p["variable"]), baustein: String(p["block"]) };
    return { finding: f, sperrt: f.severity === "error", behebung };
  });
  return { punkte, sperrt: templateActivationBlocked(findings) || findings.some((f) => f.severity === "error") };
}

/**
 * Befunde, die nur der Editor kennt: Anlage in einem Container, zwei Wahlklauseln mit demselben Schlüssel (beide hingen an
 * einem Ja/Nein), leerer vertraulicher Teil.
 */
export function editorBefunde(blocks: readonly Block[]): Finding[] {
  const out: Finding[] = [];
  const keys = new Map<string, number>();
  for (const { block, parents } of walkBlocks(blocks)) {
    if (block.type === "attachment" && parents.length) out.push({ code: "ATTACHMENT_IN_CONTAINER", severity: "error", params: { block: block.id } });
    if (block.type === "choice") keys.set(block.key, (keys.get(block.key) ?? 0) + 1);
    if (block.type === "private" && ![...walkBlocks(block.children)].some(({ block: x }) => texteVon(x).some((t) => t.length > 0) || x.type === "field" || x.type === "table")) {
      out.push({ code: "PRIVATE_PART_EMPTY", severity: "warning", params: { block: block.id } });
    }
  }
  for (const [key, n] of keys) if (n > 1) out.push({ code: "DUPLICATE_CHOICE_KEY", severity: "error", params: { key } });
  return out;
}

/** Behebung „Einfügen“: Unterschriftsfeld der Rolle ergänzen oder einen Unterschriftenblock am Ende anlegen. */
export function behebe(blocks: readonly Block[], punkt: PruefPunkt, lang: Language): Block[] {
  const b = punkt.behebung;
  if (!b || b.art === "zeigen") return [...blocks];
  if (b.art === "unterschriften-anlegen") {
    const ids = alleIds(blocks);
    return fuegeEin(blocks.filter(() => true), null, neuerBaustein("signatures", ids, lang));
  }
  if (b.art === "unterschrift") {
    const sig = [...walkBlocks(blocks)].map((x) => x.block).find((x) => x.type === "signatures");
    if (!sig) return [...blocks];
    return aendere(blocks, sig.id, (x) => x.type === "signatures" && !x.parties.includes(b.rolle) ? { ...x, parties: CONTRACT_ROLES.filter((r) => r === b.rolle || x.parties.includes(r)) } : x);
  }
  // „Satz nur zeigen, wenn ausgefüllt“: den Baustein in ein „Nur wenn“ auf das Feld setzen
  const ziel = finde(blocks, b.baustein);
  if (!ziel) return [...blocks];
  const ids = alleIds(blocks);
  const id = neueId(ids);
  const huelle: Block = { id, type: "condition", when: { src: "custom", path: b.feld }, children: [ziel] };
  return aendere(blocks, b.baustein, () => huelle);
}

/** Klartext der Befunde, die nur der Baukasten kennt (übrige über findingText in pruefen.ts). */
export function baukastenBefundText(f: Finding, defs: readonly CustomVariableDef[]): string | null {
  const p = f.params ?? {};
  switch (f.code) {
    case "INVALID_BLOCKS": return t("Die Bausteine sind unvollständig (etwa eine wählbare Klausel ohne Frage oder ein leerer Container). Bitte die markierte Stelle ergänzen.", "The blocks are incomplete (for example an optional clause without a question or an empty container). Please complete the marked spot.");
    case "OPTIONAL_FIELD_IN_SENTENCE": return t(`Optionales Feld „${feldLabel({ src: "custom", path: String(p["variable"]) }, defs)}“ steht mitten im Satz. Ohne Wert bleibt eine Lücke.`, `Optional field “${feldLabel({ src: "custom", path: String(p["variable"]) }, defs)}” is in the middle of a sentence. Without a value a gap remains.`);
    case "PRIVATE_PART_NAMES_HIDDEN_PARTY": return t(`Der vertrauliche Teil nennt Pflichten oder Angaben ${p["role"] === "brand" ? "der Marke" : p["role"] === "talent" ? "des Creators" : "der Agentur"}, die ihn nicht sieht.`, `The confidential part names duties or details of ${ROLE_LABEL[p["role"] as ContractRole] ?? p["role"]}, who does not see it.`);
    case "PRIVATE_PART_AGENCY": return t("Die Agentur muss den vertraulichen Teil immer sehen.", "The agency must always see the confidential part.");
    case "NO_SIGNERS": return t("Die Vorlage hat noch keine Unterschriften. Wer unterschreibt, steht im Baustein „Unterschriften“.", "The template has no signatures yet. Whoever signs is listed in the “Signatures” block.");
    case "CONDITION_STRUCTURE": return t("Ein „Nur wenn“ ist nicht vollständig. Bitte im Baukasten neu anlegen.", "An “only if” is incomplete. Please create it again in the builder.");
    case "ATTACHMENT_IN_CONTAINER": return t("Eine Anlage steht in einem „Nur wenn“ oder einer wählbaren Klausel. Anlagen gehören auf die oberste Ebene, sonst stimmt ihre Nummer nicht.", "An annex is inside an “only if” or an optional clause. Annexes belong on the top level, otherwise their number is wrong.");
    case "DUPLICATE_CHOICE_KEY": return t("Zwei wählbare Klauseln hängen an derselben Frage. Bitte eine davon entfernen und neu einfügen.", "Two optional clauses depend on the same question. Please remove one of them and insert it again.");
    case "PRIVATE_PART_EMPTY": return t("Der vertrauliche Teil ist leer. Bitte Text ergänzen oder den Teil entfernen.", "The confidential part is empty. Please add text or remove the part.");
    case "TEMPLATE_TOO_LARGE": return t("Die Vorlage ist zu groß (höchstens 400.000 Zeichen je Teil, 600 Bausteine, 200 Felder).", "The template is too large (at most 400,000 characters per part, 600 blocks, 200 fields).");
    default: return null;
  }
}

// ---------------------------------------------------------------------------
// Umwandeln (Vorlagen aus dem Bestand)
// ---------------------------------------------------------------------------

export interface Umwandlung { blocks: Block[]; freitext: number; ok: boolean }

/** Bestandsvorlage (HTML) in Bausteine; was sich nicht zuordnen lässt, bleibt „Freitext aus alter Vorlage“. */
export function wandleUm(v: { body_html: string; annex_html: string | null; annex_hidden_from: ContractRole[]; variables: CustomVariableDef[]; language: Language }): Umwandlung {
  const r = convertTemplate({ bodyHtml: v.body_html, annexHtml: v.annex_html, annexHiddenFrom: v.annex_hidden_from, variables: v.variables, language: v.language });
  return { blocks: r.blocks, freitext: r.legacy, ok: r.problem === null && validateBlocks(r.blocks) === null };
}

// ---------------------------------------------------------------------------
// Text im Editor: Läufe ↔ bearbeitbares HTML (Chips nicht bearbeitbar)
// ---------------------------------------------------------------------------

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const MARK_TAG: Record<InlineMark, string> = { b: "strong", i: "em", u: "u" };

/** Beschriftung eines Chips (Feld oder „Nur wenn“ im Satz). */
export type ChipLabel = (node: Extract<Inline, { t: "field" | "if" }>) => { text: string; art: FeldArt | "wenn" };

/**
 * Läufe als HTML für das bearbeitbare Feld: Text escaped, Auszeichnungen als strong/em/u, Zeilenumbruch als br, Chips als
 * nicht bearbeitbare span mit dem Knoten als JSON (data-node). Nie ein Platzhalter-Code im Text.
 */
export function inlineZuEditorHtml(nodes: readonly Inline[], label: ChipLabel): string {
  let out = "";
  for (const n of nodes) {
    const marks = n.t === "if" ? [] : n.marks ?? [];
    const open = marks.map((m) => `<${MARK_TAG[m]}>`).join("");
    const close = [...marks].reverse().map((m) => `</${MARK_TAG[m]}>`).join("");
    // Zeilenumbruch und Leerraum im Text zeigt der Vertrag als ein Leerzeichen (HTML): im Editor genauso (angezeigt = gedruckt)
    if (n.t === "text") out += open + esc(n.v.replace(/[ \t\r\n]+/g, " ")) + close;
    else if (n.t === "br") out += `${open}<br>${close}`;
    else {
      const l = label(n);
      out += `${open}<span class="bk-chip bk-chip-${l.art}" contenteditable="false" data-node="${esc(JSON.stringify(n))}">${esc(l.text)}</span>${close}`;
    }
  }
  // Nach einem Chip am Ende ein Textknoten, damit die Schreibmarke hinter den Chip kann (wird beim Lesen entfernt)
  const last = nodes[nodes.length - 1];
  if (last && (last.t === "field" || last.t === "if")) out += "\u200b";
  return out;
}

/** Minimaler DOM-Knoten (Element oder Text), damit die Rückübersetzung ohne Browser testbar ist. */
export interface MiniNode {
  nodeType: number;
  nodeName: string;
  nodeValue: string | null;
  childNodes: ArrayLike<MiniNode>;
  getAttribute?(name: string): string | null;
}

const TAG_MARK: Record<string, InlineMark> = { STRONG: "b", B: "b", EM: "i", I: "i", U: "u" };
const BLOCK_TAGS = new Set(["DIV", "P", "LI", "H1", "H2", "H3", "H4"]);

/**
 * Inhalt des bearbeitbaren Felds zurück in Läufe. Text bleibt Text (auch ein getipptes „{{“ wird nie ein Platzhalter),
 * Chips kommen aus data-node (nur Feld und „Nur wenn“, sonst verworfen), Absätze des Browsers werden Zeilenumbrüche,
 * alles andere zählt nur mit seinem Text.
 */
export function editorZuInline(root: MiniNode): Inline[] {
  const out: Inline[] = [];
  const push = (n: Inline) => {
    const last = out[out.length - 1];
    if (n.t === "text" && last?.t === "text" && sameMarks(last.marks, n.marks)) { last.v += n.v; return; }
    out.push(n);
  };
  const walk = (node: MiniNode, marks: InlineMark[], first: boolean) => {
    if (node.nodeType === 3) {
      // Browser schreiben Leerzeichen am Rand und doppelte Leerzeichen als U+00A0; ein echtes geschütztes Leerzeichen zwischen
      // zwei Zeichen (etwa „§\u00a01“) bleibt erhalten
      const raw = (node.nodeValue ?? "").replace(/\u200b/g, "");
      const v = raw.replace(/\u00a0/g, (_m: string, i: number) => (i === 0 || i === raw.length - 1 || raw[i - 1] === " " || raw[i + 1] === " " || raw[i - 1] === "\u00a0" || raw[i + 1] === "\u00a0" ? " " : "\u00a0"));
      if (v) push(marks.length ? { t: "text", v, marks: [...marks] } : { t: "text", v });
      return;
    }
    if (node.nodeType !== 1) return;
    const name = node.nodeName.toUpperCase();
    if (name === "BR") { push(marks.length ? { t: "br", marks: [...marks] } : { t: "br" }); return; }
    const rawNode = node.getAttribute?.("data-node");
    if (rawNode) {
      const chip = chipAus(rawNode);
      if (chip) push(chip.t === "field" && marks.length ? { ...chip, marks: [...marks] } : chip.t === "field" ? stripMarks(chip) : chip);
      // Chips, die der Browser in einen Chip gesetzt hat, gehen nicht verloren (Text im Chip ist nur seine Beschriftung)
      const nested = (n: MiniNode) => {
        for (const k of Array.from(n.childNodes)) {
          if (k.nodeType !== 1) continue;
          if (k.getAttribute?.("data-node")) walk(k, marks, false);
          else nested(k);
        }
      };
      nested(node);
      return;
    }
    if (BLOCK_TAGS.has(name) && !first && out.length && out[out.length - 1]!.t !== "br") push({ t: "br" });
    const m = TAG_MARK[name];
    const next = m && !marks.includes(m) ? [...marks, m] : marks;
    const kids = Array.from(node.childNodes);
    kids.forEach((k) => walk(k, next, false));
  };
  Array.from(root.childNodes).forEach((k, i) => walk(k, [], i === 0));
  // Browser hängen gern einen leeren Umbruch ans Ende
  while (out.length && out[out.length - 1]!.t === "br") out.pop();
  return out.map((n) => (n.t === "text" || n.t === "br") && n.marks ? { ...n, marks: sortMarks(n.marks) } : n.t === "field" && n.marks ? { ...n, marks: sortMarks(n.marks) } : n);
}

function stripMarks(n: Extract<Inline, { t: "field" }>): Inline {
  const { marks: _m, ...rest } = n;
  return rest;
}

function sortMarks(m: InlineMark[]): InlineMark[] {
  return (["b", "i", "u"] as InlineMark[]).filter((x) => m.includes(x));
}

function sameMarks(a: readonly InlineMark[] | undefined, b: readonly InlineMark[] | undefined): boolean {
  const x = sortMarks([...(a ?? [])]).join();
  const y = sortMarks([...(b ?? [])]).join();
  return x === y;
}

function chipAus(raw: string): Inline | null {
  try {
    const n = JSON.parse(raw) as Inline;
    if (n && (n.t === "field" || n.t === "if")) {
      // Schema-Prüfung am Einzelknoten: ein Absatz mit genau diesem Knoten
      return validateBlocks([{ id: "x", type: "paragraph", text: [n] }]) === null ? n : null;
    }
  } catch {
    /* kaputter Chip: verwerfen */
  }
  return null;
}

/** Klartext der Läufe (für Suche, Gliederung, leere Prüfung). */
export function inlineText(nodes: readonly Inline[], defs: readonly CustomVariableDef[]): string {
  return nodes.map((n) => (n.t === "text" ? n.v : n.t === "br" ? "\n" : n.t === "field" ? feldLabel(n.field, defs) : inlineText(n.then, defs))).join("");
}

/** Liste als Punkte: neuen Punkt nach `nachId`. */
export function neuerPunkt(items: readonly ListItem[], nachId: string | null, ids: Set<string>): ListItem[] {
  const id = neueId(ids); ids.add(id);
  const neu: ListItem = { id, text: [] };
  if (nachId === null) return [...items, neu];
  const i = items.findIndex((x) => x.id === nachId);
  return [...items.slice(0, i + 1), neu, ...items.slice(i + 1)];
}

/** Rollen, die im Dokument unterschreiben. */
export function unterzeichnerVon(blocks: readonly Block[]): ContractRole[] {
  const r = new Set<ContractRole>();
  for (const { block } of walkBlocks(blocks)) if (block.type === "signatures") for (const x of block.parties) r.add(x);
  return CONTRACT_ROLES.filter((x) => r.has(x));
}
